import React, {useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as ArrowIcon} from 'assets/svg/arrow-bottom.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';

import {Form, useForm} from '../../Form';
import {Icon} from '../../Icon';
import {ConfigElement} from '../types';

import {ButtonsContainer, CustomButton, CustomLabel} from './styled';

export type FiltersType = Array<{id: string; value: any}>;

interface Props {
    config: ConfigElement[];
    onChange: (filters: FiltersType) => void;
    className?: string;
    filters: FiltersType;
}

const getFilterDefaultValue = (filters: FiltersType, id: string) => {
    const filter = filters.find((filter) => filter.id === id);
    return filter ? filter.value : null;
};

const configReducer = (config: ConfigElement[], filters: FiltersType) =>
    config.reduce(
        (accumulator, elem) => ({
            ...accumulator,
            [elem.accessor]: {
                defaultValue: getFilterDefaultValue(filters, elem.accessor),
            },
        }),
        {},
    );

export const Filters = ({config, filters, onChange, className}: Props) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const formData = useForm();

    const fieldsConfig = useMemo(() => configReducer(config, filters), [config, filters]);

    const handleFormSubmit = (data: Record<string, any>) => {
        const mappedFilters = Object.entries(data)
            .filter(([, value]) => value)
            .map(([key, value]) => ({
                id: key,
                value,
            }));

        onChange(mappedFilters);
    };

    const onReset = () => {
        onChange([]);
    };

    return (
        <div className={className}>
            <CustomButton onClick={() => setIsOpen(!isOpen)}>
                {t('components.endlessList.filtration')}
                <Icon width={14} height={8} source={ArrowIcon} />
            </CustomButton>
            {isOpen && (
                <Form
                    formData={formData}
                    onSubmit={handleFormSubmit}
                    fieldsConfig={fieldsConfig}
                    maxFieldsDepth={2}
                >
                    {config.map(
                        (elem, index) =>
                            elem.Filter && (
                                <CustomLabel
                                    key={index}
                                    text={
                                        elem.parentHeader
                                            ? `${elem.parentHeader} ${elem.Header}`
                                            : elem.Header
                                    }
                                >
                                    {React.cloneElement(elem.Filter, {
                                        ...elem.Filter.props,
                                        formFieldName: elem.accessor,
                                    })}
                                </CustomLabel>
                            ),
                    )}

                    <ButtonsContainer>
                        <Button variant={ButtonVariant.Normal} type="submit">
                            {t('components.endlessList.Submit')}
                        </Button>
                        <Button variant={ButtonVariant.Text} type="reset" onClick={onReset}>
                            {t('components.endlessList.Reset')}
                        </Button>
                    </ButtonsContainer>
                </Form>
            )}
        </div>
    );
};
